/* BhiFullDossierExportDialog.jsx — iter943
 *
 * Универсален dialog за експорт на пълния BHI clinical dossier PDF.
 * Показва две toggle групи (език + audience), сваля PDF blob-a и
 * автоматично начатва download.
 *
 * Props:
 *   trigger?: node   — custom trigger; ако липсва рендира gradient button
 *   variant?: string — "primary" | "ghost" (стил на default trigger-a)
 *   testId?: string
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, Languages, Users, Sparkles } from "lucide-react";
import { useAuth } from "@/App";

const LANG_OPTIONS = [
  { key: "bg", label: "Български", flag: "🇧🇬" },
  { key: "en", label: "English",   flag: "🇬🇧" },
];

const AUDIENCE_OPTIONS = [
  { key: "clinician", label: "Клиницист",           labelEn: "Clinician",   note: "PMID/DOI, латински термини" },
  { key: "patient",   label: "Пациент",             labelEn: "Patient",     note: "Обяснения + интервенции" },
  { key: "both",      label: "Клиницист + Пациент", labelEn: "Both",        note: "Пълно клинично досие" },
];

export default function BhiFullDossierExportDialog({
  trigger,
  variant = "primary",
  testId = "bhi-dossier-export-dialog",
}) {
  const { API } = useAuth();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("bg");
  const [audience, setAudience] = useState("both");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/bhi/full-dossier/pdf`,
        { lang, audience },
        { responseType: "blob", timeout: 60000 },
      );
      // Trigger download
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `nutramilo-bhi-dossier-${lang}-${audience}-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "bg" ? "PDF готов ✓" : "PDF ready ✓");
      setOpen(false);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Export failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const defaultTrigger = (
    <Button
      data-testid="bhi-dossier-export-open"
      className={
        variant === "primary"
          ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 font-semibold shadow-lg"
          : "bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
      }
    >
      <FileDown className="w-4 h-4 mr-2" />
      {lang === "bg" ? "Пълен PDF" : "Full PDF"}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-md bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 border border-slate-200 dark:border-slate-800" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Пълно BHI клинично досие
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600 dark:text-slate-400">
            Многостраничен визуален PDF с всички 6 BHI модула — Интелигентност,
            Доказателства, Превенция, Тъканна възстановителна програма,
            Персонален коуч и Наследствен риск.
          </DialogDescription>
        </DialogHeader>

        {/* Language */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            <Languages className="w-3.5 h-3.5" /> Език
          </div>
          <div className="grid grid-cols-2 gap-2">
            {LANG_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                data-testid={`dossier-lang-${o.key}`}
                onClick={() => setLang(o.key)}
                className={`text-sm px-3 py-2 rounded-lg font-medium border transition ${
                  lang === o.key
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                {o.flag} {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Audience */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            <Users className="w-3.5 h-3.5" /> За кого е PDF-ът
          </div>
          <div className="space-y-1.5">
            {AUDIENCE_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                data-testid={`dossier-audience-${o.key}`}
                onClick={() => setAudience(o.key)}
                className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                  audience === o.key
                    ? "bg-purple-50 border-purple-400 ring-1 ring-purple-200 dark:bg-purple-500/15 dark:border-purple-500/50 dark:ring-purple-500/20"
                    : "bg-white border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {lang === "bg" ? o.label : o.labelEn}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{o.note}</div>
              </button>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={busy}
            data-testid="dossier-cancel-btn"
          >
            Отказ
          </Button>
          <Button
            onClick={handleExport}
            disabled={busy}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700"
            data-testid="dossier-generate-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Генериране…
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-2" />
                Свали PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
